import React, { useContext, useEffect, useMemo } from 'react';
import { Button, Col, DatePicker, Empty, Form, Popover, Row, Tooltip } from 'antd';
import { useCalendario } from '../hooks';
import { CalendarOutlined } from "@ant-design/icons"
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Provider as JotaiProvider } from "jotai";
import { SecurityContext } from "context/SecurityContextProvider";

const MAX_ITEMS_LEYENDA = 6;
const RUC_SDF = "20330791684";
const RUC_SDF_ENERGIA = "20512436090";

const TabCalendario = () => {
    return (
        <JotaiProvider>
            <Filtros />
            <Calendario />
        </JotaiProvider>
    );
};

const Calendario = () => {
    const { solicitudes } = useCalendario();
    const { getUsuarioAutenticado } = useContext(SecurityContext);

    const ruc = getUsuarioAutenticado().empresaSeleccionada?.ruc;
    const mostrarLeyenda = ruc === RUC_SDF || ruc === RUC_SDF_ENERGIA;

    if (solicitudes.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No existe datos. Haga clic en Mostrar Calendario" />
        )
    }

    return (
        // <InfiniteScroll
        //     dataLength={colaboradores.length}
        //     next={loadMoreColaboradores}
        //     hasMore={colaboradores.length < totalColaboradores}
        //     loader={<h4>Loading...</h4>}
        //     height="calc(100vh - 270px)"
        // >
        //     <Cabecera />
        //     {
        //         colaboradores.map(
        //             (colaborador) =>
        //                 <Fila key={colaborador.colaborador.id} colaborador={colaborador} />)
        //     }
        // </InfiniteScroll>
        <div>
            {mostrarLeyenda && <Leyenda solicitudes={solicitudes} />}
            <div style={{ height: "calc(100vh - 270px)", overflow: "auto" }}>
                <Cabecera />
                {
                    solicitudes.map(
                        (solicitud) =>
                            <Fila key={solicitud.colaborador.id} solicitud={solicitud} />)
                }
            </div>
        </div>
    )
}

const Leyenda = ({ solicitudes }) => {
    const items = useMemo(() => {
        const mapa = new Map();
        (solicitudes || []).forEach((solicitud) => {
            (solicitud.vacaciones || []).forEach((vacacion) => {
                const color = vacacion.color || "lightblue";
                if (!mapa.has(color)) {
                    mapa.set(color, vacacion.tipo?.nombre);
                }
            });
        });
        return Array.from(mapa, ([color, nombre]) => ({ color, nombre }));
    }, [solicitudes]);

    if (!items.length) {
        return null;
    }

    const visibles = items.slice(0, MAX_ITEMS_LEYENDA);
    const restantes = items.slice(MAX_ITEMS_LEYENDA);

    return (
        <div style={style.leyenda.container}>
            {visibles.map((item) => (
                <LeyendaItem key={item.color} item={item} />
            ))}
            {restantes.length > 0 && (
                <Popover
                    placement="bottomLeft"
                    content={
                        <div style={style.leyenda.popoverContent}>
                            {restantes.map((item) => (
                                <LeyendaItem key={item.color} item={item} />
                            ))}
                        </div>
                    }
                >
                    <div style={style.leyenda.masItem}>+{restantes.length} más</div>
                </Popover>
            )}
        </div>
    );
};

const LeyendaItem = ({ item }) => (
    <div style={style.leyenda.item}>
        <span style={{ ...style.leyenda.muestra, backgroundColor: item.color }} />
        <span>{item.nombre}</span>
    </div>
);

const Fila = ({ solicitud }) => {
    const { getDias, fechaSeleccionada } = useCalendario();

    return (
        <div style={style.fila.container}>
            <div style={style.fila.celdaColaborador}>
                {solicitud.colaborador.nombreCompleto}
            </div>
            <div style={{ width: "75%" }}>
                <div style={style.fila.containerDias}>
                    {solicitud?.vacaciones &&
                        solicitud.vacaciones?.map((solicitud) => (
                            <Tooltip key={solicitud.id} title={solicitud.tipo.nombre} placement="top">
                                <div
                                    style={{
                                        ...style.fila.celdaDias,
                                        left: `calc( (${solicitud.diaInicio} - 1 )  * (100% / ${fechaSeleccionada.daysInMonth()}))`,
                                        width: `calc(${solicitud.diasSolicitados} * (100% / ${fechaSeleccionada.daysInMonth()}))`,
                                        backgroundColor: solicitud.color ? solicitud.color : "lightblue"
                                    }}
                                >

                                </div>
                            </Tooltip>
                        ))}

                    {getDias().map((day) => (
                        <div key={day} style={style.dias} />
                    ))}
                </div>
            </div>
        </div>
    );
};

const Cabecera = () => {
    const { getDias, fechaSeleccionada } = useCalendario();

    return (
        <div style={style.cabecera.container}>
            <div style={style.cabecera.celdaColaborador}>
                Nombres y Apellidos
            </div>
            <div style={{ width: "75%" }}>
                <div style={style.cabecera.containerDias}>
                    {getDias().map((day) => (
                        <div key={day} style={{
                            ...style.dias,
                            flexDirection: "column"
                        }}>
                            <span>
                                {moment({
                                    year: fechaSeleccionada.year(),
                                    month: fechaSeleccionada.month(),
                                    date: day,
                                })
                                    .format("dd")
                                    .toUpperCase()}
                            </span>
                            <span>{day}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const Filtros = () => {

    const [form] = Form.useForm();
    const { fechaSeleccionada, setFormInstance, onClickBtnMostrarCalendario } = useCalendario();

    useEffect(() => {
        setFormInstance(form);
    }, []);

    return (
        <Form form={form}>
            <Row gutter={24}>
                <Col span="8">
                    <Form.Item
                        name="periodo"
                        label="Mes"
                        rules={[
                            {
                                required: true,
                                message: 'Mes es obligatorio',
                            },
                        ]}
                        initialValue={fechaSeleccionada}
                    >
                        <DatePicker
                            allowClear={false}
                            picker="month"
                            format="MMMM - YYYY"
                            placeholder="Fecha"
                            style={{ width: "100%" }}
                        />
                    </Form.Item>
                </Col>
                <Col span="8">
                    <Button type='primary' onClick={onClickBtnMostrarCalendario}>
                        <CalendarOutlined /> Mostrar Calendario
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

const style = {
    dias: {
        flex: "1",
        minWidth: "0",
        borderRight: "1px solid #ccc",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "10px",
    },
    cabecera: {
        container: {
            backgroundColor: "white",
            height: "35px",
            display: "flex",
            position: "sticky",
            top: 0,
            zIndex: 10
        },
        containerDias: {
            display: "flex",
            position: "relative",
            overflowX: "auto",
            border: "1px solid #ccc",
            borderRight: "0px",
            height: "100%",
        },
        celdaColaborador: {
            width: "25%",
            border: "1px solid #ccc",
            borderRight: "0px"
        }
    },
    fila: {
        container: {
            height: "35px",
            display: "flex"
        },
        containerDias: {
            display: "flex",
            position: "relative",
            overflowX: "auto",
            border: "1px solid #ccc",
            borderRight: "0px",
            height: "100%",
            borderTop: "0px"
        },
        celdaColaborador: {
            width: "25%",
            border: "1px solid #ccc",
            borderRight: "0px",
            borderTop: "0px"
        },
        celdaDias: {
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            height: "50%",
            zIndex: "1",
            borderRadius: "15px",
        }
    },
    leyenda: {
        container: {
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: "16px",
            padding: "0 0 10px",
            overflow: "hidden",
        },
        item: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            whiteSpace: "nowrap",
        },
        muestra: {
            display: "inline-block",
            width: "12px",
            height: "12px",
            borderRadius: "3px",
            flexShrink: 0,
        },
        masItem: {
            fontSize: "12px",
            color: "#1890ff",
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
        },
        popoverContent: {
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxHeight: "240px",
            overflowY: "auto",
            minWidth: "160px",
        }
    }
}

export default TabCalendario;